import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { SharedModuleService } from 'src/app/core/services/shared-module.service';
import { ToastrShowService } from 'src/app/core/services/toastr.service';

@Component({
  selector: 'app-new-navbar',
  templateUrl: './new-navbar.component.html',
  styleUrls: ['./new-navbar.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule]
})
export class NewNavbarComponent implements OnInit {
  private toastr = inject(ToastrShowService);
  private sharedModuleService = inject(SharedModuleService);
  selectedLoginType: 'client' | 'broker' | null = null;
  isMobile = false;
  menuOpen = false;
  showBrokerModal = false;
  brokerName = '';
  showBrokerLogin: boolean = false;
  aboutDropdownOpen = false;
  mobileAboutOpen = false;

  private closeDropdownTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.checkScreenSize();
    const subdomain = sessionStorage.getItem('subdomain');
    this.showBrokerLogin = subdomain ? true : false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkScreenSize();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.about-nav-item')) {
      this.closeAboutDropdown();
    }
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
    if (!this.isMobile) {
      this.menuOpen = false;
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  openBrokerModal(): void {
    this.showBrokerModal = !this.showBrokerModal;
  }

  // ─── About dropdown (desktop) ───────────────────────────────────────────────
  openAboutDropdown(): void {
    if (this.closeDropdownTimer) {
      clearTimeout(this.closeDropdownTimer);
      this.closeDropdownTimer = null;
    }
    this.aboutDropdownOpen = true;
  }

  closeAboutDropdown(): void {
    this.closeDropdownTimer = setTimeout(() => {
      this.aboutDropdownOpen = false;
    }, 120);
  }

  toggleAboutDropdown(): void {
    this.aboutDropdownOpen = !this.aboutDropdownOpen;
  }

  // ─── About accordion (mobile) ───────────────────────────────────────────────
  toggleMobileAbout(): void {
    this.mobileAboutOpen = !this.mobileAboutOpen;
  }

  brokerLogin(username: string): void {
    if (!username.trim()) {
      return;
    }
    const subdomain = username.toLowerCase();
    const isValidSubdomain = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/.test(subdomain);
    if (!isValidSubdomain) {
      return;
    }
    const isLocalhost = window.location.hostname.includes('localhost');
    if (!isLocalhost) {
      const origin = window.location.origin.replace(/^https?:\/\//, '');
      const loginUrl = `https://${subdomain}.${origin}/#/login`;
      window.open(loginUrl, '_blank');
    } else {
      const loginUrl = `${window.location.origin}/#/login`;
      window.open(loginUrl, '_blank');
    }
    this.closeBrokerModal();
  }

  checkUsername(username: string): void {
    this.sharedModuleService
      .checksubDomainAvailability(username)
      .subscribe((isAvailable: boolean) => {
        if (isAvailable) {
          this.toastr.showError('Organization name does not exist');
        } else {
          this.brokerLogin(username);
        }
      });
  }

  login(): void {
    const loginUrl = `${window.location.origin}/#/login`;
    window.open(loginUrl, '_blank');
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  selectLoginType(type: 'client' | 'broker'): void {
    this.selectedLoginType = type;
  }

  isLoginEnabled(): boolean {
    if (this.selectedLoginType === 'client') {
      return true;
    } else if (this.selectedLoginType === 'broker') {
      return this.brokerName.trim().length > 0;
    }
    return false;
  }

  handleLogin(): void {
    if (this.selectedLoginType === 'client') {
      this.login();
    } else if (this.selectedLoginType === 'broker') {
      this.checkUsername(this.brokerName);
    }
  }

  onEnterPress(): void {
    if (this.isLoginEnabled()) {
      this.handleLogin();
    }
  }

  onModalBackdropClick(event: MouseEvent): void {
    this.closeBrokerModal();
  }

  closeBrokerModal(): void {
    this.showBrokerModal = false;
    this.brokerName = '';
    this.selectedLoginType = null;
  }

  restrictUsernameInput(event: KeyboardEvent): void {
    const key = event.key;
    const allowedRegex = /^[a-zA-Z0-9-]$/;

    if (
      !allowedRegex.test(key) &&
      !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)
    ) {
      event.preventDefault();
    }
  }
}
